import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { auth } from "../firebase";
import { db } from "../firebase";
import { collection, addDoc, getDocs } from "firebase/firestore";

const SignUp = () => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [userClass, setUserClass] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [batches, setBatches] = useState<any[]>([]);
  const [filteredBatches, setFilteredBatches] = useState<any[]>([]);
  const [selectedBatch, setSelectedBatch] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch all batches on mount
    const fetchBatches = async () => {
      try {
        const snapshot = await getDocs(collection(db, "batches"));
        const batchList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setBatches(batchList);
      } catch (err) {
        // Ignore batch load errors for signup
      }
    };
    fetchBatches();
  }, []);

  useEffect(() => {
    if (userClass) {
      setFilteredBatches(batches.filter(b => b.class === userClass));
      setSelectedBatch("");
    } else {
      setFilteredBatches([]);
      setSelectedBatch("");
    }
  }, [userClass, batches]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !userClass || !selectedBatch || !email || !password) {
      return setError("Please fill in all required fields");
    }
    try {
      setError("");
      setLoading(true);
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      await addDoc(collection(db, "users"), {
        uid: userCredential.user.uid,
        name,
        phone,
        class: userClass,
        batchId: selectedBatch,
        batchName: (batches.find(b => b.id === selectedBatch)?.name || ""),
        email,
        password,
        role: "student",
        status: "pending",
        createdAt: new Date(),
      });
      await import("firebase/auth").then(({ signOut }) => signOut(auth));
      navigate("/pending-approval");
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-400/40 via-indigo-200/60 to-blue-100/90 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white/30 backdrop-blur-lg border border-white/40 rounded-3xl shadow-2xl p-8 glassmorphism">
        <div className="flex flex-col items-center">
          <img
            src="/assets/images/logo.png"
            alt="Logo"
            className="w-20 h-20 mb-2 rounded-full shadow-md"
          />
          <h2 className="mt-2 text-center text-3xl font-extrabold text-blue-800">
            Create your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Or{" "}
            <Link
              to="/login"
              className="font-medium text-blue-600 hover:text-blue-500"
            >
              sign in to your existing account
            </Link>
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        <form className="mt-8 space-y-8" onSubmit={handleSubmit}>
          <div className="flex flex-col gap-6">
            <div>
              <label htmlFor="name" className="sr-only">
                Name
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                className="block w-full px-4 py-3 border border-gray-200 placeholder-gray-500 text-gray-900 rounded-2xl shadow focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 bg-white/70 backdrop-blur-md transition-all duration-200"
                placeholder="Full Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="phone" className="sr-only">
                Phone
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                className="block w-full px-4 py-3 border border-gray-200 placeholder-gray-500 text-gray-900 rounded-2xl shadow focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 bg-white/70 backdrop-blur-md transition-all duration-200"
                placeholder="Phone (optional)"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="class" className="sr-only">
                Class
              </label>
              <select
                id="class"
                name="class"
                required
                className="block w-full px-4 py-3 border border-gray-200 text-gray-900 rounded-2xl shadow focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 bg-white/70 backdrop-blur-md transition-all duration-200"
                value={userClass}
                onChange={(e) => setUserClass(e.target.value)}
              >
                <option value="">Select Class</option>
                <option value="IX">Class IX</option>
                <option value="X">Class X</option>
                <option value="XI">Class XI</option>
                <option value="XII">Class XII</option>
              </select>
            </div>
            {/* Batch selection (only if class is selected) */}
            {userClass && (
              <div>
                <label className="block text-sm font-medium text-blue-900 mb-1">Batch</label>
                <select
                  className="block w-full px-4 py-3 border border-gray-200 text-gray-900 rounded-2xl shadow focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 bg-white/70 backdrop-blur-md transition-all duration-200"
                  value={selectedBatch}
                  onChange={e => setSelectedBatch(e.target.value)}
                  required
                >
                  <option value="">Select Batch</option>
                  {filteredBatches.length === 0 ? (
                    <option value="" disabled>No batch available for this class</option>
                  ) : (
                    filteredBatches.map(batch => (
                      <option key={batch.id} value={batch.id}>{batch.name}</option>
                    ))
                  )}
                </select>
              </div>
            )}
            <div>
              <label htmlFor="email-address" className="sr-only">
                Email address
              </label>
              <input
                id="email-address"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="block w-full px-4 py-3 border border-gray-200 placeholder-gray-500 text-gray-900 rounded-2xl shadow focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 bg-white/70 backdrop-blur-md transition-all duration-200"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  className="block w-full px-4 py-3 border border-gray-200 placeholder-gray-500 text-gray-900 rounded-2xl shadow focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 bg-white/70 backdrop-blur-md pr-12 transition-all duration-200"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-400 hover:text-blue-600 focus:outline-none"
                  onClick={() => setShowPassword((prev: boolean) => !prev)}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-5.523 0-10-4.477-10-10 0-1.657.403-3.221 1.125-4.575m2.12-2.12A9.956 9.956 0 0112 3c5.523 0 10 4.477 10 10 0 1.657-.403 3.221-1.125 4.575m-2.12 2.12A9.956 9.956 0 0112 21c-1.657 0-3.221-.403-4.575-1.125"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-5.523 0-10-4.477-10-10 0-1.657.403-3.221 1.125-4.575m2.12-2.12A9.956 9.956 0 0112 3c5.523 0 10 4.477 10 10 0 1.657-.403 3.221-1.125 4.575m-2.12 2.12A9.956 9.956 0 0112 21c-1.657 0-3.221-.403-4.575-1.125"
                      />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 mt-4 text-white font-bold text-base rounded-2xl shadow-lg bg-gradient-to-r from-blue-500 via-blue-600 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 backdrop-blur-md transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? "Signing up..." : "Sign Up"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SignUp;
